(async () => {
  const { 
    loadScript,
    loadStyle
  } = await import(window.theme.modules.utils);

  const baseComponent = await import(window.theme.modules.baseComponent);

  const isChromeForIOs145 = () => {
    const userAgent = window.navigator.userAgent;
    const isChromeForIOs = /CriOS/i.test(userAgent);
    if (isChromeForIOs) {
      const iOsMatch = userAgent.match(
        /(.+)(iPhone|iPad|iPod)(.+)OS[\s|\_](\d+)\_?(\d+)?[\_]?(\d+)?.+/i
      );
      if (iOsMatch && iOsMatch.length >= 6) {
        const iOsVersionMajor = parseInt(iOsMatch[4], 10);
        const iOsVersionMinor = parseInt(iOsMatch[5], 10);
        if (iOsVersionMajor >= 14 && iOsVersionMinor >= 6) {
          return true;
        }
      }
    }
    return false;
  };

  if (customElements.get('slide-show')) return;

  customElements.define('slide-show', class extends baseComponent.default {
    elements = {
      sliderContainer: '[data-slider-container]',
      slides: '*[data-slide-variants]',
      nextButton: '[data-slider-button-next]',
      prevButton: '[data-slider-button-prev]',
      pagination: '[data-slider-pagination]',
      thumbnails: 'slideshow-thumbnails'
    };

    async render() {
      if (this._initialized || this._initializing) return;
      this._initializing = true;

      try {
        const canLazyInit =
          this.hasAttribute('lazy-init') &&
          !(window.Shopify && window.Shopify.designMode) &&
          !this.hasAttribute('sync-slider') &&
          !this.hasAttribute('thumbnails');

        if (canLazyInit) {
          await this._waitUntilNearViewport();
        }

        await this._initSwiper();

        const delay = this.getAttribute('autoplay-delay') * 1000;
        const autoplay = this.hasAttribute('use-autoplay') && { delay };
        const navType = this.getAttribute('navigation-type');
        const usePagination = navType === 'pagination' || navType === 'pagination_buttons';
        const useButtons = navType === 'buttons' || navType === 'pagination_buttons';
        const swiperParams = {
          delay,
          autoplay,
          initialSlide: Math.max(0, Number(this.getAttribute('initial-slide')) || 0),
          pagination: usePagination && { el: this.$pagination, clickable: true },
          navigation: useButtons && {
            nextEl: this.$nextButton,
            prevEl: this.$prevButton
          },
          zoom: this.hasAttribute('zoom'),
          breakpoints: this.hasAttribute('desktop-no-swipe') && {
            992: {
              allowTouchMove: false
            }
          },
          cssMode: isChromeForIOs145()
        };

        if (this.hasAttribute('sync-slider')) {
          this.syncSlider = document.getElementById(this.getAttribute('sync-slider'));
          swiperParams.initialSlide = this.syncSlider ? this.syncSlider.getCurrentSlide() : 0;
        }

        this.swiper = new window.Swiper(this.$sliderContainer, swiperParams);
        this._initialized = true;

        if (this.hasAttribute('observe-slide-type')) {
          this._setMediaType(this.swiper.activeIndex);
          this.swiper.on('slideChange', ({ activeIndex }) => {
            this._setMediaType(activeIndex);
          });
        }

        if (this.hasAttribute('zoom')) {
          this.swiper.on('zoomChange', (_, scale) => {
            scale === 1 ? this.removeAttribute('zoomed') : this.setAttribute('zoomed', '');
          });
        }

        if (this.hasAttribute('thumbnails') && this.$thumbnails) {
          this.$thumbnails.onSliderLoad(this);
        }

        if (this.syncSlider) {
          this.swiper.on('slideChange', () => {
            this.syncSlider.slideTo(this.getCurrentSlide());
          });
        }
      } finally {
        this._initializing = false;
      }
    }

    async _waitUntilNearViewport() {
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 800;
      const rect = this.getBoundingClientRect();

      // Already close enough to the viewport: initialize immediately.
      if (rect.top <= viewportHeight + 500 && rect.bottom >= -200) return;

      // Older browsers: fall back to immediate initialization.
      if (!('IntersectionObserver' in window)) return;

      await new Promise((resolve) => {
        const observer = new IntersectionObserver((entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect();
            resolve();
          }
        }, {
          root: null,
          rootMargin: '500px 0px',
          threshold: 0
        });

        observer.observe(this);
      });
    }

    async _initSwiper() {
      // Keep Swiper CSS behavior unchanged; only delay the below-fold constructor.
      loadStyle(window.theme.styles.Swiper);
      await loadScript(window.theme.scripts.Swiper, 'swiper');
    }

    getCurrentSlide() {
      return this.swiper ? this.swiper.activeIndex : 0;
    }

    slideTo(index) {
      if (this.swiper) this.swiper.slideTo(index);
    }

    _setMediaType(slideIndex) {
      if (!this.swiper || !this.swiper.slides[slideIndex]) return;
      this.dataset.slideMediaType = this.swiper.slides[slideIndex].dataset.mediaType;
    }
  });
})();